// Dashboard Layout Helper Functions

import type { Layout } from 'react-grid-layout';
import type { DashboardWidget } from '../DashboardGrid';
import { GRID_CONFIG } from '../constants';

const DEFAULT_MAX_HEIGHT = 20;

const getWidgetWidthConstraints = (widget: DashboardWidget, cols: number) => {
  const minW = Math.min(widget.minSize?.w || 2, cols);
  const maxW = Math.min(widget.maxSize?.w || cols, cols);
  const defaultW = Math.min(widget.defaultSize.w, maxW);

  return {
    minW,
    maxW,
    w: Math.max(minW, defaultW)
  };
};

const getWidgetHeightConstraints = (widget: DashboardWidget) => {
  const minH = widget.minSize?.h || 2;
  const maxH = widget.maxSize?.h || DEFAULT_MAX_HEIGHT;
  const defaultH = Math.min(widget.defaultSize.h, maxH);

  return {
    minH,
    maxH,
    h: Math.max(minH, defaultH)
  };
};

const createLayoutItem = (
  widget: DashboardWidget,
  x: number,
  y: number,
  cols: number
): Layout => {
  const { minW, maxW, w } = getWidgetWidthConstraints(widget, cols);
  const { minH, maxH, h } = getWidgetHeightConstraints(widget);

  return {
    i: widget.id,
    x,
    y,
    w,
    h,
    minW,
    minH,
    maxW,
    maxH,
  };
};

// Check if a widget can be placed at a specific position
export const canPlaceWidget = (
  occupied: boolean[][],
  x: number,
  y: number,
  w: number,
  h: number,
  cols: number
): boolean => {
  if (x + w > cols || y + h > occupied.length) return false;
  
  for (let i = y; i < y + h; i++) {
    for (let j = x; j < x + w; j++) {
      if (occupied[i][j]) return false;
    }
  }
  return true;
};

// Mark a widget position as occupied
export const placeWidget = (
  occupied: boolean[][],
  x: number,
  y: number,
  w: number,
  h: number
): void => {
  for (let i = y; i < y + h; i++) {
    for (let j = x; j < x + w; j++) {
      occupied[i][j] = true;
    }
  }
};

// Find the best position for a widget
export const findBestPosition = (
  occupied: boolean[][],
  w: number,
  h: number,
  cols: number
): { x: number; y: number } | null => {
  // Try to find position with minimum y first (top-left strategy)
  for (let y = 0; y < occupied.length - h + 1; y++) {
    for (let x = 0; x <= cols - w; x++) {
      if (canPlaceWidget(occupied, x, y, w, h, cols)) {
        return { x, y };
      }
    }
  }
  return null;
};

// Generate auto layout for widgets using bin packing algorithm
export const generateAutoLayout = (
  widgets: DashboardWidget[],
  cols: number = GRID_CONFIG.COLS.lg
): Layout[] => {
  const packed: Layout[] = [];
  let cursorX = 0;
  let cursorY = 0;
  let currentRowHeight = 0;
  
  widgets.forEach((widget) => {
    const item = createLayoutItem(widget, cursorX, cursorY, cols);

    if (cursorX > 0 && cursorX + item.w > cols) {
      cursorX = 0;
      cursorY += currentRowHeight;
      currentRowHeight = 0;
      item.x = cursorX;
      item.y = cursorY;
    }

    packed.push(item);
    cursorX += item.w;
    currentRowHeight = Math.max(currentRowHeight, item.h);
  });
  
  // log.debug('layoutHelpers', '🔧 Generated auto layout:', packed);
  return packed;
};

// Merge saved layouts with new widgets
export const mergeLayouts = (
  savedLayouts: Layout[],
  currentWidgets: DashboardWidget[],
  cols: number
): Layout[] => {
  // log.debug('layoutHelpers', '🔀 Merging layouts:', { savedLayouts, currentWidgets: currentWidgets.map(w => w.id), cols });
  
  if (!savedLayouts || !Array.isArray(savedLayouts)) {
    // log.debug('layoutHelpers', '⚠️ No saved layouts, generating auto layout');
    return generateAutoLayout(currentWidgets, cols);
  }
  
  const result: Layout[] = [];
  const processedIds = new Set<string>();
  
  // Create a map of widget definitions for easy lookup
  const widgetMap = new Map(currentWidgets.map(w => [w.id, w]));
  const occupied: boolean[][] = Array(GRID_CONFIG.MAX_ROWS).fill(null).map(() => Array(cols).fill(false));
  
  // First, keep all saved layouts so hidden widgets retain their last known positions
  savedLayouts.forEach((item) => {
    const widget = widgetMap.get(item.i);
    const requestedMinW = Math.min(widget?.minSize?.w || item.minW || 2, cols);
    const requestedMaxW = Math.max(
      requestedMinW,
      Math.min(widget?.maxSize?.w || item.maxW || cols, cols),
    );
    const safeW = Math.min(Math.max(requestedMinW, item.w), requestedMaxW);
    const safeX = Math.min(Math.max(0, item.x), cols - safeW);
    const requestedMinH = widget?.minSize?.h || item.minH || 2;
    const requestedMaxH = Math.max(
      requestedMinH,
      widget?.maxSize?.h || item.maxH || DEFAULT_MAX_HEIGHT,
    );
    const safeH = Math.min(Math.max(requestedMinH, item.h), requestedMaxH);
    const safeY = Math.max(0, item.y);
    const mergedItem = {
      ...item,
      x: safeX,
      y: safeY,
      w: safeW,
      h: safeH,
      minW: requestedMinW,
      minH: requestedMinH,
      maxW: requestedMaxW,
      maxH: requestedMaxH,
    };

    result.push(mergedItem);
    processedIds.add(item.i);

    const occupiedHeight = Math.max(1, Math.min(mergedItem.h, occupied.length - safeY));

    if (safeX < cols && safeY < occupied.length) {
      placeWidget(occupied, safeX, safeY, safeW, occupiedHeight);
    }
  });
  
  // Then, add layouts for new widgets that aren't in saved layouts
  currentWidgets.forEach(widget => {
    if (!processedIds.has(widget.id)) {
      const { w } = getWidgetWidthConstraints(widget, cols);
      const { h } = getWidgetHeightConstraints(widget);

      let position = null;
      if (
        widget.defaultPosition &&
        canPlaceWidget(occupied, widget.defaultPosition.x, widget.defaultPosition.y, w, h, cols)
      ) {
        position = widget.defaultPosition;
      } else {
        position = findBestPosition(occupied, w, h, cols);
      }

      if (position) {
        const newItem = createLayoutItem(widget, position.x, position.y, cols);

        result.push(newItem);
        processedIds.add(widget.id);
        placeWidget(occupied, position.x, position.y, w, h);
      }
    }
  });
  
  // log.debug('layoutHelpers', '📐 Final merged layout:', result);
  return result;
};
